import { useEffect, useState } from 'react';
import { AnimatePresence, m } from 'motion/react';
import { formatBytes } from '../core/format';
import { engine, useApp } from '../state/store';
import { useSession } from './hooks/useSession';
import { Check, Gear, Info, Keyboard, KindIcon, List, Warning, WifiSlash, X } from './parts/Icons';
import { stateLabel } from './state-copy';

export function LogoMark({ size = 22 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 512 512" aria-hidden="true" className="logo">
      <rect width="512" height="512" rx="112" fill="currentColor" />
      <g fill="none" strokeLinecap="round" strokeWidth="44">
        <path d="M112 196h132" stroke="var(--paper)" />
        <path d="M292 196h108" stroke="var(--ink-3)" />
        <path d="M112 316h40" stroke="var(--ink-3)" />
        <path d="M196 316h204" stroke="var(--amber)" />
      </g>
    </svg>
  );
}

export function TopBar() {
  const activeId = useApp((s) => s.activeId);
  const setActive = useApp((s) => s.setActive);
  const setPanel = useApp((s) => s.setPanel);
  const queueOpen = useApp((s) => s.queueOpen);
  const setQueueOpen = useApp((s) => s.setQueueOpen);
  const count = useApp((s) => Object.keys(s.records).length);
  const { record } = useSession(activeId);

  return (
    <header className="topbar">
      <button
        type="button"
        className="brand"
        onClick={() => setActive(null)}
        aria-label="OneFlow, back to start"
      >
        <LogoMark />
        <span className="brand__name">OneFlow</span>
      </button>

      {record && (
        <div className="chipbar" aria-label="Current file">
          <KindIcon kind={record.kind} className="chipbar__icon" />
          <span className="chipbar__name">{record.name}</span>
          <span className="chipbar__host mono">{record.host}</span>
          <span className="chipbar__state">
            <span className={`dot dot--${record.state}`} aria-hidden="true" />
            {stateLabel(record.state)}
          </span>
        </div>
      )}

      <nav className="topbar__nav" aria-label="App">
        {activeId && (
          <button type="button" className="btn btn--quiet" onClick={() => setActive(null)}>
            New link
          </button>
        )}
        <button
          type="button"
          className="btn btn--quiet topbar__queue"
          onClick={() => setQueueOpen(!queueOpen)}
          aria-expanded={queueOpen}
          aria-label={`Files on this device, ${count}`}
        >
          <List /> <span className="mono">{count}</span>
        </button>
        <button
          type="button"
          className="btn btn--quiet btn--icon"
          onClick={() => setPanel('help')}
          aria-label="Keyboard shortcuts"
        >
          <Keyboard />
        </button>
        <button
          type="button"
          className="btn btn--quiet btn--icon"
          onClick={() => setPanel('settings')}
          aria-label="Settings"
        >
          <Gear />
        </button>
      </nav>
    </header>
  );
}

export function Banners({ updater }: { updater: (() => Promise<void>) | null }) {
  const online = useApp((s) => s.online);
  const records = useApp((s) => s.records);
  const bootError = useApp((s) => s.bootError);
  const [usage, setUsage] = useState<{ usage: number; quota: number } | null>(null);
  const downloading = Object.values(records).some(
    (r) => r.state === 'active' || r.state === 'probing',
  );

  useEffect(() => {
    let alive = true;
    const check = () =>
      void engine.storageEstimate().then((e) => {
        if (alive && e.usage !== null && e.quota !== null)
          setUsage({ usage: e.usage, quota: e.quota });
      });
    check();
    const t = setInterval(check, 30_000);
    return () => {
      alive = false;
      clearInterval(t);
    };
  }, []);

  const tight = usage && usage.quota > 0 && usage.usage / usage.quota > 0.85;

  return (
    <div className="banners">
      {bootError && (
        <div className="banner banner--error" role="alert">
          <Warning />{' '}
          <span>
            <strong>{bootError.summary}</strong> {bootError.action}
          </span>
        </div>
      )}
      {!online && (
        <div className="banner" role="status">
          <WifiSlash />{' '}
          <span>
            You are offline. Saved files still open. Downloads continue when you reconnect.
          </span>
        </div>
      )}
      {tight && usage && (
        <div className="banner banner--warn" role="status">
          <Warning />{' '}
          <span>
            Storage is nearly full: {formatBytes(usage.usage)} of about {formatBytes(usage.quota)}{' '}
            used. Delete files you no longer need.
          </span>
        </div>
      )}
      {updater && (
        <div className="banner" role="status">
          <Check />
          <span>A new version of OneFlow is ready.</span>
          <button
            type="button"
            className="btn btn--quiet"
            disabled={downloading}
            onClick={() => void updater()}
          >
            {downloading ? 'Reload after downloads pause' : 'Reload now'}
          </button>
        </div>
      )}
    </div>
  );
}

export function Toasts() {
  const toasts = useApp((s) => s.toasts);
  const dismiss = useApp((s) => s.dismissToast);
  return (
    <div className="toasts" aria-live="off">
      <AnimatePresence>
        {toasts.map((t) => (
          <m.div
            key={t.id}
            layout
            className={`toast toast--${t.kind}`}
            initial={{ opacity: 0, y: 12, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 6 }}
            transition={{ duration: 0.2, ease: [0.16, 1, 0.3, 1] }}
            role={t.kind === 'error' ? 'alert' : 'status'}
          >
            {t.kind === 'error' ? (
              <Warning className="toast__icon" aria-hidden="true" />
            ) : t.kind === 'success' ? (
              <Check className="toast__icon" aria-hidden="true" />
            ) : (
              <Info className="toast__icon" aria-hidden="true" />
            )}
            <div className="toast__text">
              <p className="toast__title">{t.title}</p>
              {t.body && <p className="toast__body">{t.body}</p>}
            </div>
            <button
              type="button"
              className="btn btn--quiet btn--icon"
              onClick={() => dismiss(t.id)}
              aria-label="Dismiss"
            >
              <X />
            </button>
          </m.div>
        ))}
      </AnimatePresence>
    </div>
  );
}

/** One polite live region for state changes. It never speaks byte counts. */
export function Announcer() {
  const msg = useApp((s) => s.announcement);
  return (
    <div className="sr-only" role="status" aria-live="polite" aria-atomic="true">
      {msg}
    </div>
  );
}
